/**
 * Commerce domain event definitions — catalog v3 (commerce-platform Phase 2a).
 *
 * `commerce:probe-created` is the Phase 0 exit-criteria event: the commerce
 * skeleton writes a CatalogProbe + outbox row in one transaction and the
 * outbox relay publishes this event; the audit service tailer ingests it
 * (auditable:true), proving the event -> audit chain end to end.
 */
import {
  defineEvent,
  asEventRecord,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface ProbeCreatedPayload {
  probeId: string;
  orgId: string;
  name: string;
}

export interface ProductEventPayload {
  productId: string;
  orgId: string;
}

export interface DiscountEventPayload {
  discountId: string;
  orgId: string;
}

export interface DiscountRedeemedPayload extends DiscountEventPayload {
  redemptionId: string;
}

export interface TaxRateUpdatedPayload {
  taxRateId: string;
  orgId: string;
}

export interface RulesVersionBumpedPayload {
  orgId: string;
  version: string;
}

const probeCreatedSchema: EventSchema<ProbeCreatedPayload> = {
  parse(data: unknown): ProbeCreatedPayload {
    const record = asEventRecord(data, 'commerce:probe-created');
    return {
      probeId: requireString(record.probeId, 'probeId', 'commerce:probe-created'),
      orgId: requireString(record.orgId, 'orgId', 'commerce:probe-created'),
      name: requireString(record.name, 'name', 'commerce:probe-created'),
    };
  },
};

export const COMMERCE_PROBE_CREATED_EVENT: EventDefinition<ProbeCreatedPayload> =
  defineEvent<ProbeCreatedPayload>({
    type: 'commerce:probe-created',
    version: 1,
    schema: probeCreatedSchema,
    auditable: true,
    description:
      'An org-scoped catalog probe was created (permanent commerce smoke/validation vehicle; payload mirrors the outbox write).',
    since: '1.0.0',
  });

const productEventSchema = (type: string): EventSchema<ProductEventPayload> => ({
  parse(data: unknown): ProductEventPayload {
    const record = asEventRecord(data, type);
    return {
      productId: requireString(record.productId, 'productId', type),
      orgId: requireString(record.orgId, 'orgId', type),
    };
  },
});

const discountEventSchema = (type: string): EventSchema<DiscountEventPayload> => ({
  parse(data: unknown): DiscountEventPayload {
    const record = asEventRecord(data, type);
    return {
      discountId: requireString(record.discountId, 'discountId', type),
      orgId: requireString(record.orgId, 'orgId', type),
    };
  },
});

const discountRedeemedSchema: EventSchema<DiscountRedeemedPayload> = {
  parse(data: unknown): DiscountRedeemedPayload {
    const record = asEventRecord(data, 'commerce:discount-redeemed');
    return {
      discountId: requireString(record.discountId, 'discountId', 'commerce:discount-redeemed'),
      orgId: requireString(record.orgId, 'orgId', 'commerce:discount-redeemed'),
      redemptionId: requireString(record.redemptionId, 'redemptionId', 'commerce:discount-redeemed'),
    };
  },
};

const taxRateUpdatedSchema: EventSchema<TaxRateUpdatedPayload> = {
  parse(data: unknown): TaxRateUpdatedPayload {
    const record = asEventRecord(data, 'commerce:tax-rate-updated');
    return {
      taxRateId: requireString(record.taxRateId, 'taxRateId', 'commerce:tax-rate-updated'),
      orgId: requireString(record.orgId, 'orgId', 'commerce:tax-rate-updated'),
    };
  },
};

const rulesVersionBumpedSchema: EventSchema<RulesVersionBumpedPayload> = {
  parse(data: unknown): RulesVersionBumpedPayload {
    const record = asEventRecord(data, 'commerce:rules-version-bumped');
    return {
      orgId: requireString(record.orgId, 'orgId', 'commerce:rules-version-bumped'),
      version: requireString(record.version, 'version', 'commerce:rules-version-bumped'),
    };
  },
};

const defineProductEvent = (
  type: 'commerce:product-created' | 'commerce:product-updated' | 'commerce:product-archived',
  description: string,
): EventDefinition<ProductEventPayload> => defineEvent<ProductEventPayload>({
  type,
  version: 1,
  schema: productEventSchema(type),
  auditable: true,
  description,
  since: '1.2.0',
});

const defineDiscountEvent = (
  type: 'commerce:discount-created' | 'commerce:discount-updated',
  description: string,
): EventDefinition<DiscountEventPayload> => defineEvent<DiscountEventPayload>({
  type,
  version: 1,
  schema: discountEventSchema(type),
  auditable: true,
  description,
  since: '1.2.0',
});

export const COMMERCE_PRODUCT_CREATED_EVENT = defineProductEvent(
  'commerce:product-created',
  'An org-scoped catalog product was created.',
);

export const COMMERCE_PRODUCT_UPDATED_EVENT = defineProductEvent(
  'commerce:product-updated',
  'An org-scoped catalog product was updated.',
);

export const COMMERCE_PRODUCT_ARCHIVED_EVENT = defineProductEvent(
  'commerce:product-archived',
  'An org-scoped catalog product was archived.',
);

export const COMMERCE_DISCOUNT_CREATED_EVENT = defineDiscountEvent(
  'commerce:discount-created',
  'An org-scoped discount template was created.',
);

export const COMMERCE_DISCOUNT_UPDATED_EVENT = defineDiscountEvent(
  'commerce:discount-updated',
  'An org-scoped discount template was updated.',
);

export const COMMERCE_DISCOUNT_REDEEMED_EVENT: EventDefinition<DiscountRedeemedPayload> =
  defineEvent<DiscountRedeemedPayload>({
    type: 'commerce:discount-redeemed',
    version: 1,
    schema: discountRedeemedSchema,
    auditable: true,
    description: 'An org-scoped discount template was redeemed.',
    since: '1.2.0',
  });

export const COMMERCE_TAX_RATE_UPDATED_EVENT: EventDefinition<TaxRateUpdatedPayload> =
  defineEvent<TaxRateUpdatedPayload>({
    type: 'commerce:tax-rate-updated',
    version: 1,
    schema: taxRateUpdatedSchema,
    auditable: true,
    description: 'An org-scoped tax rate was updated.',
    since: '1.2.0',
  });

export const COMMERCE_RULES_VERSION_BUMPED_EVENT: EventDefinition<RulesVersionBumpedPayload> =
  defineEvent<RulesVersionBumpedPayload>({
    type: 'commerce:rules-version-bumped',
    version: 1,
    schema: rulesVersionBumpedSchema,
    auditable: true,
    description: 'An org-scoped commerce rules watermark was bumped.',
    since: '1.2.0',
  });

export const COMMERCE_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [COMMERCE_PROBE_CREATED_EVENT.type]: COMMERCE_PROBE_CREATED_EVENT,
  [COMMERCE_PRODUCT_CREATED_EVENT.type]: COMMERCE_PRODUCT_CREATED_EVENT,
  [COMMERCE_PRODUCT_UPDATED_EVENT.type]: COMMERCE_PRODUCT_UPDATED_EVENT,
  [COMMERCE_PRODUCT_ARCHIVED_EVENT.type]: COMMERCE_PRODUCT_ARCHIVED_EVENT,
  [COMMERCE_DISCOUNT_CREATED_EVENT.type]: COMMERCE_DISCOUNT_CREATED_EVENT,
  [COMMERCE_DISCOUNT_UPDATED_EVENT.type]: COMMERCE_DISCOUNT_UPDATED_EVENT,
  [COMMERCE_DISCOUNT_REDEEMED_EVENT.type]: COMMERCE_DISCOUNT_REDEEMED_EVENT,
  [COMMERCE_TAX_RATE_UPDATED_EVENT.type]: COMMERCE_TAX_RATE_UPDATED_EVENT,
  [COMMERCE_RULES_VERSION_BUMPED_EVENT.type]: COMMERCE_RULES_VERSION_BUMPED_EVENT,
};
